import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  ArrowRight,
  ShieldCheck,
  House,
  ClipboardText,
  CameraPlus,
  FilePdf,
  Signature,
  UsersThree,
  Buildings,
  ChartLineUp,
  EnvelopeSimple,
  Phone,
  MapPin,
} from "@phosphor-icons/react";
import { TRENT_LOGO_URL } from "@/components/Logo";

const SERVICES = [
  { icon: ClipboardText, title: "Property Inventory Reports", body: "Comprehensive inventory of every item, fixture and fitting in the property." },
  { icon: House, title: "Move-In Inspections", body: "Detailed handover assessment with photo evidence and signed condition record." },
  { icon: Buildings, title: "Move-Out Inspections", body: "End-of-tenancy comparison report — protect deposits with clear evidence." },
  { icon: ChartLineUp, title: "Mid-Term Inspections", body: "Routine in-tenancy reviews to catch issues early and document upkeep." },
  { icon: CameraPlus, title: "Photo Evidence Documentation", body: "Categorised photo galleries for exterior, rooms, appliances, meters and damages." },
  { icon: UsersThree, title: "Tenant & Landlord Records", body: "Full directory of landlords, agents and tenants linked to each property." },
  { icon: Signature, title: "Digital Signature Completion", body: "Tenants and landlords sign reports digitally — no printing, no posting." },
];

const HERO_IMG =
  "https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&w=1600&q=80";

export default function Landing() {
  const { user } = useAuth();
  const adminHref = user?.role === "admin" ? "/admin-dashboard" : "/admin-login";

  return (
    <div className="min-h-screen bg-white text-[#0F172A]">
      <Nav adminHref={adminHref} isAdmin={user?.role === "admin"} />
      <Hero adminHref={adminHref} />
      <About />
      <Services />
      <Contact />
      <Footer />
    </div>
  );
}

const Nav = ({ adminHref, isAdmin }) => (
  <header className="sticky top-0 z-40 bg-white/90 backdrop-blur border-b border-[#E5E7EB]">
    <div className="mx-auto max-w-[1280px] px-6 h-16 flex items-center justify-between">
      <Link to="/" className="flex items-center gap-3" data-testid="landing-brand">
        <img src={TRENT_LOGO_URL} alt="Trent Property Management" className="h-9 w-auto" />
        <div className="hidden sm:block leading-tight border-l border-[#E5E7EB] pl-3">
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#475569] font-semibold">
            Property Management
          </div>
        </div>
      </Link>
      <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-[#475569]">
        <a href="#about" className="hover:text-[#0F172A]">About</a>
        <a href="#services" className="hover:text-[#0F172A]">Services</a>
        <a href="#contact" className="hover:text-[#0F172A]">Contact</a>
        <Link to="/admin-login" className="hover:text-[#0F172A]">Admin login</Link>
      </nav>
      <div className="flex items-center gap-2">
        {isAdmin ? (
          <Link to="/admin-dashboard" className="trent-btn-primary" data-testid="landing-open-dashboard">
            <ShieldCheck weight="bold" size={14} /> Open dashboard
          </Link>
        ) : (
          <>
            <Link to="/login" className="trent-btn-secondary hidden sm:inline-flex" data-testid="landing-signin">
              Sign in
            </Link>
            <Link to="/signup" className="trent-btn-secondary hidden md:inline-flex" data-testid="landing-signup">
              Sign up
            </Link>
            <Link to={adminHref} className="trent-btn-primary" data-testid="landing-admin-signin">
              <ShieldCheck weight="bold" size={14} /> Admin
            </Link>
          </>
        )}
      </div>
    </div>
  </header>
);

const Hero = ({ adminHref }) => (
  <section className="relative overflow-hidden">
    <div className="absolute inset-0">
      <img src={HERO_IMG} alt="Property" className="w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0F172A]/95 via-[#0F172A]/90 to-[#0F172A]/65" />
    </div>
    <div className="relative mx-auto max-w-[1280px] px-6 py-24 md:py-36 text-white">
      <div className="inline-flex items-center gap-2 px-3 py-1 border border-[#16A34A]/40 bg-[#16A34A]/10 text-[#6EE7B7] text-[10px] uppercase tracking-[0.3em] rounded-full">
        <ShieldCheck size={12} weight="bold" /> Trent Property Management · Ireland
      </div>
      <h1 className="trent-heading mt-5 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter max-w-3xl leading-[1.05]">
        Professional property inventory &amp;<br className="hidden md:inline" />{" "}
        <span className="text-[#16A34A]">inspection services.</span>
      </h1>
      <p className="mt-6 max-w-2xl text-base md:text-lg text-white/75 leading-relaxed">
        Trent Property Management provides professional property inventory, move-in inspection,
        move-out inspection, mid-term inspection, photo documentation, and property condition
        reporting services for landlords, tenants, letting agents, and property managers.
      </p>
      <div className="mt-8 flex flex-wrap gap-3">
        <Link to="/signup" className="trent-btn-accent text-base" data-testid="hero-customer-cta">
          Customer Sign Up <ArrowRight weight="bold" size={16} />
        </Link>
        <Link to="/login" className="trent-btn-secondary text-base bg-white/10 border-white/20 text-white hover:bg-white/20" data-testid="hero-customer-signin">
          Customer Sign In
        </Link>
        <Link to={adminHref} className="trent-btn-secondary text-base bg-white/10 border-white/20 text-white hover:bg-white/20" data-testid="hero-admin-cta">
          <ShieldCheck size={14} weight="bold" /> Admin Sign In
        </Link>
      </div>
    </div>
  </section>
);

const About = () => (
  <section id="about" className="py-24 bg-white">
    <div className="mx-auto max-w-[1280px] px-6 grid md:grid-cols-12 gap-12 items-start">
      <div className="md:col-span-5">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#16A34A] font-semibold mb-3">
          About us
        </div>
        <h2 className="trent-heading text-3xl md:text-4xl font-black tracking-tighter text-[#0F172A]">
          Inspection &amp; inventory specialists for the Irish rental market.
        </h2>
      </div>
      <div className="md:col-span-7 space-y-5 text-[#334155] text-base leading-relaxed">
        <p>
          We work with landlords, letting agents, property managers and tenants across Ireland to
          produce clear, evidence-backed reports for every stage of a tenancy — from first
          inventory to final deposit return.
        </p>
        <p>
          Every report is built around Irish rental standards: Eircode-aware property records,
          BER fields, RTB-grade photo evidence and structured room-by-room condition assessments.
        </p>
        <p>
          Our digital signature flow lets tenants and landlords countersign reports remotely, so
          you can finalise the paperwork the same day the inspection is done.
        </p>
        <div className="grid grid-cols-3 gap-4 pt-4">
          <Stat n="7" l="Step inspection flow" />
          <Stat n="100%" l="RTB-grade documentation" />
          <Stat n="∞" l="Photo evidence per inspection" />
        </div>
      </div>
    </div>
  </section>
);

const Stat = ({ n, l }) => (
  <div className="border-l-2 border-[#16A34A] pl-4">
    <div className="text-2xl font-black text-[#0F172A] trent-heading">{n}</div>
    <div className="text-[10px] uppercase tracking-[0.2em] text-[#64748B] mt-1">{l}</div>
  </div>
);

const Services = () => (
  <section id="services" className="py-24 bg-[#F8FAFC] border-y border-[#E5E7EB]">
    <div className="mx-auto max-w-[1280px] px-6">
      <div className="max-w-2xl">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#16A34A] font-semibold mb-3">
          Our services
        </div>
        <h2 className="trent-heading text-3xl md:text-4xl font-black tracking-tighter text-[#0F172A]">
          What we deliver.
        </h2>
        <p className="mt-4 text-[#475569]">
          Every service is documented to RTB-tribunal standard and digitally archived for both
          landlord and tenant.
        </p>
      </div>
      <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {SERVICES.map((s) => (
          <div
            key={s.title}
            className="p-6 border border-[#E5E7EB] rounded-lg bg-white hover:border-[#16A34A] transition-colors"
            data-testid={`service-${s.title.replace(/\s+/g, "-").toLowerCase()}`}
          >
            <div className="w-10 h-10 rounded-md bg-[#0F172A] text-white flex items-center justify-center mb-4">
              <s.icon size={20} weight="duotone" />
            </div>
            <h3 className="trent-heading font-bold text-[#0F172A] mb-2">{s.title}</h3>
            <p className="text-sm text-[#475569] leading-relaxed">{s.body}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const CONTACT_EMAIL = "info@trent.irish";
const CONTACT_PHONE_DISPLAY = "+353 851 868 884";
const CONTACT_PHONE_TEL = "+353851868884";
const CONTACT_ADDRESS = "48, Oldtown Woods, Celbridge, Kildare";

const Contact = () => (
  <section id="contact" className="py-24 bg-white">
    <div className="mx-auto max-w-[1280px] px-6 grid md:grid-cols-2 gap-12 items-start">
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#16A34A] font-semibold mb-3">
          Contact
        </div>
        <h2 className="trent-heading text-3xl md:text-4xl font-black tracking-tighter text-[#0F172A]">
          Get in touch.
        </h2>
        <p className="mt-4 text-[#475569] max-w-md">
          For booking inspections, inventory work or to discuss your portfolio, reach out below.
          We typically respond within one business day.
        </p>
        <div className="mt-8 space-y-4 text-sm">
          <a href={`mailto:${CONTACT_EMAIL}`} className="flex items-center gap-3 text-[#0F172A] hover:text-[#16A34A]" data-testid="contact-email-link">
            <span className="w-9 h-9 rounded-md bg-[#F1F5F9] flex items-center justify-center text-[#16A34A]">
              <EnvelopeSimple size={18} weight="duotone" />
            </span>
            {CONTACT_EMAIL}
          </a>
          <a href={`tel:${CONTACT_PHONE_TEL}`} className="flex items-center gap-3 text-[#0F172A] hover:text-[#16A34A]" data-testid="contact-phone-link">
            <span className="w-9 h-9 rounded-md bg-[#F1F5F9] flex items-center justify-center text-[#16A34A]">
              <Phone size={18} weight="duotone" />
            </span>
            {CONTACT_PHONE_DISPLAY}
          </a>
          <a
            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(CONTACT_ADDRESS)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-start gap-3 text-[#0F172A] hover:text-[#16A34A]"
            data-testid="contact-address-link"
          >
            <span className="w-9 h-9 rounded-md bg-[#F1F5F9] flex items-center justify-center text-[#16A34A] shrink-0">
              <MapPin size={18} weight="duotone" />
            </span>
            <span>{CONTACT_ADDRESS}</span>
          </a>
        </div>
        <div className="mt-6 rounded-lg overflow-hidden border border-[#E5E7EB] aspect-[16/10] bg-[#F1F5F9]" data-testid="contact-map">
          <iframe
            title="Trent office location"
            src={`https://www.google.com/maps?q=${encodeURIComponent(CONTACT_ADDRESS)}&output=embed`}
            width="100%"
            height="100%"
            style={{ border: 0 }}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      </div>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          const name = e.target.elements.name.value || "website";
          const message = e.target.elements.message.value || "";
          window.location.href =
            `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(`Enquiry from ${name}`)}&body=${encodeURIComponent(message)}`;
        }}
        className="bg-[#F8FAFC] border border-[#E5E7EB] rounded-lg p-6 grid gap-4"
        data-testid="contact-form"
      >
        <label className="block">
          <span className="text-xs font-semibold uppercase tracking-wider text-[#64748B]">Name</span>
          <input name="name" required className="trent-input mt-1" data-testid="contact-name" />
        </label>
        <label className="block">
          <span className="text-xs font-semibold uppercase tracking-wider text-[#64748B]">Email</span>
          <input name="email" type="email" required className="trent-input mt-1" data-testid="contact-email" />
        </label>
        <label className="block">
          <span className="text-xs font-semibold uppercase tracking-wider text-[#64748B]">Message</span>
          <textarea name="message" rows={4} required className="trent-input mt-1" data-testid="contact-message" />
        </label>
        <button type="submit" className="trent-btn-primary" data-testid="contact-submit">
          Send enquiry <ArrowRight weight="bold" size={14} />
        </button>
      </form>
    </div>
  </section>
);

const Footer = () => (
  <footer className="border-t border-[#E5E7EB] py-10 text-sm text-[#475569] bg-white">
    <div className="mx-auto max-w-[1280px] px-6 grid md:grid-cols-3 gap-6 items-start">
      <div className="flex items-center gap-3">
        <img src={TRENT_LOGO_URL} alt="Trent" className="h-8" />
        <span>© {new Date().getFullYear()} Trent Property Management · Ireland</span>
      </div>
      <div className="text-xs space-y-1" data-testid="footer-contact">
        <div>
          <a href={`mailto:${CONTACT_EMAIL}`} className="hover:text-[#16A34A]">{CONTACT_EMAIL}</a>
        </div>
        <div>
          <a href={`tel:${CONTACT_PHONE_TEL}`} className="hover:text-[#16A34A]">{CONTACT_PHONE_DISPLAY}</a>
        </div>
        <div>{CONTACT_ADDRESS}</div>
      </div>
      <div className="flex items-center gap-6 md:justify-end">
        <a href="#about">About</a>
        <a href="#services">Services</a>
        <a href="#contact">Contact</a>
        <Link to="/admin-login">Admin</Link>
      </div>
    </div>
  </footer>
);
